import { createBrowserRouter, Navigate, Outlet, redirect, useLocation, useRouteError } from 'react-router-dom'
import React, { Suspense, useEffect } from 'react'
import WritingDailyProvider from '@/features/training/streaks/WritingDailyProvider'
import StreakProvider from '@/features/training/streaks/StreakProvider'
import { AuthProvider } from '@/features/auth/useAuth'
import AppTopbar from '@/components/AppTopbar'
import MobileBottomNav from '@/components/MobileBottomNav'
import { FlagProvider } from '@/components/FlagContextMenu'
import FeatureFlagBootstrap from '@/features/feature-flags/FeatureFlagBootstrap'
import FlagGate from '@/features/feature-flags/FlagGate'
import RhymeRollout from '@/features/training/rhyme-lab/RhymeRollout'
import { isChunkLoadError } from '@/lib/chunkRecovery'
import { captureClientError } from '@/lib/sentry'
import { apiFetch } from '@/lib/api'

const LandingPage = React.lazy(() => import('@/features/landing/LandingPage'))
const DashboardPage = React.lazy(() => import('@/features/dashboard/DashboardPage'))
const DocumentsPage = React.lazy(() => import('@/features/documents/DocumentsPage'))
const NewDocumentPage = React.lazy(() => import('@/features/documents/NewDocumentPage'))
const DocumentDetailPage = React.lazy(() => import('@/features/documents/DocumentDetailPage'))
const LoginPage = React.lazy(() => import('@/features/auth/LoginPage'))
const SettingsPage = React.lazy(() => import('@/features/settings/SettingsPage'))
const RwgPage = React.lazy(() => import('@/features/rwg/RwgPage'))
const FlashcardsPage = React.lazy(() => import('@/features/flashcards/FlashcardsPage'))
const SortPage = React.lazy(() => import('@/features/sort/SortPage'))
const AlignmentDemoPage = React.lazy(() => import('@/features/alignment-demo/AlignmentDemoPage'))
const TrainingPage = React.lazy(() => import('@/features/training/TrainingPage'))
const RhymeTrainingPage = React.lazy(() => import('@/features/training/RhymeTrainingPage'))
const RecallPage = React.lazy(() => import('@/features/training/RecallPage'))
const SeasonSetupPage = React.lazy(() => import('@/features/training/SeasonSetupPage'))
const CategoryPickerPage = React.lazy(() => import('@/features/training/CategoryPickerPage'))
const SessionDetailPage = React.lazy(() => import('@/features/training/SessionDetailPage'))
const CategoryProgressPage = React.lazy(() => import('@/features/training/CategoryProgressPage'))
const RhymeLabPage = React.lazy(() => import('@/features/training/rhyme-lab/RhymeLabPage'))
const RhythmLabPage = React.lazy(() => import('@/features/training/rhythm/RhythmLabPage'))
const SeasonPage = React.lazy(() => import('@/features/training/season/SeasonPage'))
const RhetoricPage = React.lazy(() => import('@/features/training/RhetoricPage'))
const TrainingDisciplinePage = React.lazy(() => import('@/features/training/TrainingDisciplinePage'))
const WallPage = React.lazy(() => import('@/features/training/WallPage'))
const StoryTalkPage = React.lazy(() => import('@/features/training/StoryTalkPage'))
const RhymeDbPage = React.lazy(() => import('@/features/rhyme-db/RhymeDbPage'))
const ReviewPage = React.lazy(() => import('@/features/review/ReviewPage'))
const BulkReviewPage = React.lazy(() => import('@/features/review/BulkReviewPage'))
const SiftPage = React.lazy(() => import('@/features/sift/SiftPage'))
const SongAnnotationPage = React.lazy(() => import('@/features/song-annotation/SongAnnotationPage'))
const AdminPage = React.lazy(() => import('@/features/admin/AdminPage'))
const CorpusAnalyzerPage = React.lazy(() => import('@/features/admin/CorpusAnalyzerPage'))
const RhymeSearchPage = React.lazy(() => import('@/features/admin/RhymeSearchPage'))
const TestsetPage = React.lazy(() => import('@/features/admin/TestsetPage'))
// Distinct from the user-facing TrainingPage in features/training/ (the
// recall/drill game). This is the admin curation page.
const AdminTrainingPage = React.lazy(() => import('@/features/admin/TrainingPage'))
const RhymeLabAdminPage = React.lazy(() => import('@/features/admin/RhymeLabAdminPage'))
const TrainingPromptsPage = React.lazy(() => import('@/features/admin/TrainingPromptsPage'))
const AdminFlagsPage = React.lazy(() => import('@/features/feature-flags/AdminFlagsPage'))
const PrivacyPage = React.lazy(() => import('@/features/legal/PrivacyPage'))
const TermsPage = React.lazy(() => import('@/features/legal/TermsPage'))
const NotFound = React.lazy(() => import('@/components/NotFound'))

async function requireAuth() {
  try {
    const res = await apiFetch('/auth/me')
    if (!res.ok) return redirect('/login')
  } catch {
    return redirect('/login')
  }
  return null
}

async function requireAdmin() {
  try {
    const res = await apiFetch('/auth/me')
    if (!res.ok) return redirect('/login')
    const data = await res.json()
    if (data.role !== 'admin') return redirect('/dashboard')
  } catch {
    return redirect('/login')
  }
  return null
}

function Layout() {
  // AppTopbar is the global chrome for every page. Two routes opt out:
  //   - /dashboard*: renders its own grid shell (sidebar + breadcrumb topbar).
  //   - /login:      bespoke unauth flow, no need for any global chrome.
  // Auth-aware: AppTopbar renders the back-to-Dashboard / chips / gear
  // arrangement for signed-in users and a brand + Sign in pill for guests.
  const { pathname } = useLocation()
  const hideTopbar = pathname.startsWith('/dashboard') || pathname === '/login'
  // The bottom-nav is mobile-only chrome (CSS-gated at ≤900px) and auth-aware
  // (renders null when signed out). We always hide it on /login since the
  // login flow has its own minimal shell.
  const hideBottomNav = pathname === '/login'
  return (
    <AuthProvider>
      <FlagProvider>
      <FeatureFlagBootstrap />
      <StreakProvider><WritingDailyProvider>
      {!hideTopbar && <AppTopbar />}
      <main className="flex-1 min-h-0 flex flex-col overflow-y-auto">
        <Suspense fallback={null}>
          <Outlet />
        </Suspense>
      </main>
      {!hideBottomNav && <MobileBottomNav />}
      </WritingDailyProvider></StreakProvider>
      </FlagProvider>
    </AuthProvider>
  )
}

function ErrorBoundary() {
  const error = useRouteError()
  const outdated = isChunkLoadError(error)
  useEffect(() => { captureClientError(error) }, [error])
  return (
    <div className="flex flex-col items-center justify-center flex-1 p-8" style={{ backgroundColor: '#1a1310' }}>
      <div style={{
        backgroundColor: '#fef9c3',
        padding: '32px 28px',
        boxShadow: '0 8px 16px rgba(0,0,0,0.35), 0 4px 6px rgba(0,0,0,0.2), -2px 6px 10px rgba(0,0,0,0.15), 2px 6px 10px rgba(0,0,0,0.15)',
        maxWidth: '340px',
        width: '100%',
        transform: 'rotate(-1.5deg)',
      }}>
        <div style={{ fontSize: '32px', marginBottom: '8px', lineHeight: 1 }}>:/</div>
        <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#2a1f18', marginBottom: '8px' }}>
          {outdated ? 'Refresh to continue' : 'Something went wrong'}
        </h2>
        <p style={{ fontSize: '13px', color: '#6b5740', marginBottom: '20px', lineHeight: 1.5 }}>
          {outdated ? 'This page could not load. Refresh to get the latest version.' : 'An unexpected error occurred.'}
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              backgroundColor: '#5c4a38',
              color: '#f5e6d3',
              fontWeight: 600,
              fontSize: '13px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {outdated ? 'Refresh page' : 'Try again'}
          </button>
          <a
            href="/"
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1.5px solid #c4a472',
              color: '#5c4a38',
              fontWeight: 600,
              fontSize: '13px',
              textDecoration: 'none',
            }}
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        loader: async () => {
          try {
            const res = await apiFetch('/auth/me')
            if (res.ok) {
              const data = await res.json()
              if (data.onboardingVersion < 1) {
                if (data.documentCount === 0) return redirect('/documents/new?onboarding=1')
                const docsRes = await apiFetch('/api/documents')
                if (docsRes.ok) {
                  const docs = await docsRes.json()
                  if (docs.length > 0) return redirect(`/documents/${docs[0].id}`)
                }
                return redirect('/documents/new')
              }
              return redirect('/dashboard')
            }
          } catch {}
          return null
        },
        element: <LandingPage />,
      },
      {
        path: 'dashboard',
        loader: requireAuth,
        element: <DashboardPage />,
      },
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'documents',
        loader: requireAuth,
        element: <DocumentsPage />,
      },
      {
        path: 'documents/new',
        loader: requireAuth,
        element: <NewDocumentPage />,
      },
      {
        path: 'documents/:id',
        loader: requireAuth,
        element: <DocumentDetailPage />,
      },
      {
        path: 'tools/rwg',
        element: (
          <FlagGate flag="tools.rwg">
            <RwgPage />
          </FlagGate>
        ),
      },
      {
        path: 'tools/flashcards',
        element: (
          <FlagGate flag="dashboard.new-to-wexabet">
            <FlashcardsPage />
          </FlagGate>
        ),
      },
      {
        path: 'tools/alignment',
        loader: requireAdmin,
        element: <AlignmentDemoPage />,
      },
      {
        path: 'tools/rhyme-db',
        element: <RhymeDbPage />,
      },
      {
        path: 'tools/review',
        loader: requireAdmin,
        element: <BulkReviewPage />,
      },
      {
        path: 'tools/review/legacy',
        loader: requireAdmin,
        element: <ReviewPage />,
      },
      {
        path: 'admin',
        loader: requireAdmin,
        element: <AdminPage />,
      },
      {
        path: 'annotations/:projectId?',
        element: <FlagGate flag="song-annotation.access"><SongAnnotationPage /></FlagGate>,
      },
      {
        path: 'admin/song-annotation/:projectId?',
        loader: requireAdmin,
        element: <FlagGate flag="admin.song-annotation"><SongAnnotationPage /></FlagGate>,
      },
      {
        path: 'admin/sift',
        loader: requireAdmin,
        element: <SiftPage />,
      },
      {
        path: 'admin/corpus',
        loader: requireAdmin,
        element: <CorpusAnalyzerPage />,
      },
      {
        path: 'admin/rhyme-search',
        loader: requireAdmin,
        element: <RhymeSearchPage />,
      },
      {
        path: 'admin/testset',
        loader: requireAdmin,
        element: <TestsetPage />,
      },
      {
        path: 'admin/training',
        loader: requireAdmin,
        element: <AdminTrainingPage />,
      },
      { path: 'admin/rhyme-lab', loader: requireAdmin, element: <RhymeLabAdminPage /> },
      { path: 'admin/prompts', loader: requireAdmin, element: <TrainingPromptsPage /> },
      {
        path: 'admin/flags',
        loader: requireAdmin,
        element: <AdminFlagsPage />,
      },
      {
        path: 'games/sort',
        element: (
          <FlagGate flag="dashboard.new-to-wexabet">
            <SortPage />
          </FlagGate>
        ),
      },
      {
        path: 'training',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <TrainingPage />
          </FlagGate>
        ),
      },
      { path: 'training/season', loader: requireAuth, element: <FlagGate flag="dashboard.training"><FlagGate flag="training.rhyme-lab"><SeasonPage /></FlagGate></FlagGate> },
      {
        path: 'training/rhyme',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<RhymeTrainingPage />}><RhymeLabPage /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/rhythm',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhythmLabPage />
          </FlagGate>
        ),
      },
      { path: 'training/rhetoric', loader: requireAuth, element: <FlagGate flag="dashboard.training"><RhetoricPage /></FlagGate> },
      { path: 'training/rhetoric/legacy-writing', loader: requireAuth, element: <FlagGate flag="dashboard.training"><FlagGate flag="training.rhyme-lab"><RhymeLabPage activity="writing" /></FlagGate></FlagGate> },
      ...['delivery'].map((discipline) => ({
        path: `training/${discipline}`,
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <TrainingDisciplinePage discipline={discipline} />
          </FlagGate>
        ),
      })),
      {
        path: 'training/wall',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <WallPage />
          </FlagGate>
        ),
      },
      {
        path: 'training/recall',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<RecallPage />}><Navigate to="/training/rhyme" replace /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/season/setup',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<SeasonSetupPage />}><Navigate to="/training/rhyme" replace /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/season/setup/:char',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<CategoryPickerPage />}><Navigate to="/training/rhyme" replace /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/session/:id',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<SessionDetailPage />}><Navigate to="/training/rhyme" replace /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/category/:char',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <RhymeRollout legacy={<CategoryProgressPage />}><Navigate to="/training/rhyme" replace /></RhymeRollout>
          </FlagGate>
        ),
      },
      {
        path: 'training/story-talk',
        loader: requireAuth,
        element: (
          <FlagGate flag="dashboard.training">
            <StoryTalkPage />
          </FlagGate>
        ),
      },
      {
        path: 'settings',
        loader: requireAuth,
        element: <SettingsPage />,
      },
      {
        path: 'privacy',
        element: <PrivacyPage />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
])
